import { FormEvent, ReactNode, useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { Textarea } from "@/components/catalyst/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpIcon, RefreshCwIcon, SparklesIcon } from "lucide-react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useRelocationDealChat } from "@/app/Relocations/Hooks/useRelocationDealChat";
import { RelocationDealChatMessage } from "@/app/Relocations/GraphQL/relocationDealChatQuery";
import { DealAnalysisAnswer } from "@/app/Relocations/Components/DealAnalysisAnswer";

/** The assistant's name, so the shared thread reads as one voice. */
const ANALYST = "Scout";
const ANALYST_ROLE = "Imoova Virtual Assistant";

/** Rating has its own button, because it writes the score back to the listing. */
const SUGGESTIONS = [
  "Will anyone book it?",
  "How does it compare to the others on this route?",
  "What would make it sell faster?",
];

export function RelocationDealChatDialog({
  relocationId,
}: {
  relocationId: string;
}) {
  const chat = useRelocationDealChat(relocationId);
  const [draft, setDraft] = useState("");

  async function ask(message: string) {
    const question = message.trim();

    if (question === "" || chat.isStreaming) {
      return;
    }

    setDraft("");
    await chat.send(question);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void ask(draft);
  }

  const hasThread = chat.messages.length > 0 || chat.pendingQuestion !== null;

  return (
    <BaseDialog
      size="3xl"
      title={
        <span className="flex items-center gap-3">
          <AnalystAvatar isThinking={chat.isStreaming} />
          <span className="flex items-center gap-2">
            {ANALYST}
            <span className="rounded-full bg-zinc-950/5 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:bg-white/10 dark:text-zinc-400">
              {ANALYST_ROLE}
            </span>
            <Badge color="purple">beta</Badge>
          </span>
        </span>
      }
      subtitle="Is it a good deal, will it book, and what should the supplier change?"
    >
      <div className="space-y-4">
        {hasThread || chat.isLoading ? (
          <div
            ref={chat.threadRef}
            className="max-h-[32rem] space-y-5 overflow-y-auto pr-1"
          >
            {chat.isLoading ? <Skeleton className="h-24 w-full" /> : null}

            {chat.messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {chat.pendingQuestion !== null ? (
              <Turn author="You">
                <Bubble role="user">{chat.pendingQuestion}</Bubble>
              </Turn>
            ) : null}

            {chat.isStreaming || chat.streamedReply !== "" ? (
              <Turn author={ANALYST}>
                <Bubble role="assistant">
                  {chat.streamedReply === "" ? (
                    <ThinkingDots />
                  ) : (
                    <>
                      <DealAnalysisAnswer content={chat.streamedReply} />
                      <span className="mt-1 inline-block h-4 w-1.5 animate-pulse rounded-full bg-indigo-500/70 align-middle" />
                    </>
                  )}
                </Bubble>
              </Turn>
            ) : null}

            {chat.error !== null ? (
              <p className="text-sm text-red-600">{chat.error}</p>
            ) : null}

            <div key={chat.messages.length} ref={chat.pinOnMount} />
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            color="indigo"
            isLoading={chat.isStreaming}
            onClick={() => void chat.rateDeal()}
            LeadingIcon={RefreshCwIcon}
          >
            Re-rate this deal
          </Button>

          {SUGGESTIONS.map((suggestion) => (
            <motion.button
              key={suggestion}
              type="button"
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              disabled={chat.isStreaming}
              onClick={() => void ask(suggestion)}
              className="rounded-full border border-zinc-950/10 bg-white px-3 py-1.5 text-sm text-zinc-700 shadow-sm transition-colors hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:border-indigo-400/60 dark:hover:text-indigo-300"
            >
              {suggestion}
            </motion.button>
          ))}
        </div>

        <form className="flex items-end gap-2" onSubmit={onSubmit}>
          <Textarea
            rows={2}
            value={draft}
            placeholder={`Ask ${ANALYST} about this deal…`}
            disabled={chat.isStreaming}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void ask(draft);
              }
            }}
          />
          <Button
            type="submit"
            color="indigo"
            isLoading={chat.isStreaming}
            disabled={draft.trim() === ""}
            LeadingIcon={ArrowUpIcon}
          >
            Ask
          </Button>
        </form>
      </div>
    </BaseDialog>
  );
}

function AnalystAvatar({ isThinking }: { isThinking: boolean }) {
  return (
    <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm">
      <SparklesIcon className="h-5 w-5" />
      {isThinking ? (
        <motion.span
          className="absolute inset-0 rounded-full ring-2 ring-indigo-400"
          animate={{ opacity: [0.7, 0, 0.7], scale: [1, 1.35, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
        />
      ) : null}
    </span>
  );
}

function ThinkingDots() {
  return (
    <span className="flex items-center gap-2 text-sm text-zinc-500">
      Reading the numbers
      <span className="flex gap-1">
        {[0, 1, 2].map((dot) => (
          <motion.span
            key={dot}
            className="h-1.5 w-1.5 rounded-full bg-indigo-500"
            animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: dot * 0.15,
            }}
          />
        ))}
      </span>
    </span>
  );
}

/**
 * No entrance animation: a finished stream is replaced by its stored copy, and
 * animating the stored one would read as the answer flashing and remounting.
 */
function ChatMessage({ message }: { message: RelocationDealChatMessage }) {
  return (
    <div>
      <Turn
        author={
          message.role === "user" ? (message.user?.name ?? "Imoova") : ANALYST
        }
        at={message.created_at}
      >
        <Bubble role={message.role}>
          {message.role === "assistant" ? (
            <DealAnalysisAnswer content={message.content} />
          ) : (
            message.content
          )}
        </Bubble>
      </Turn>
    </div>
  );
}

function Turn({
  author,
  at,
  children,
}: {
  author: string;
  at?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-2 text-xs text-zinc-500">
        <span className="font-medium text-zinc-600 dark:text-zinc-400">
          {author}
        </span>
        {at !== undefined ? (
          <span>{dayjs(at).format("D MMM, h:mma")}</span>
        ) : null}
      </p>
      {children}
    </div>
  );
}

function Bubble({ role, children }: { role: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        "whitespace-pre-wrap rounded-xl px-4 py-3 text-sm",
        role === "user"
          ? "bg-zinc-100 text-zinc-900 dark:bg-white/10 dark:text-white"
          : "border border-l-2 border-zinc-950/5 border-l-indigo-500/60 bg-zinc-50 text-zinc-900 dark:border-white/10 dark:border-l-indigo-400/60 dark:bg-white/5 dark:text-white",
      )}
    >
      {children}
    </div>
  );
}
